import React, { useMemo, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import ToggleButton from 'components/Shared/Forms/ToggleButton'
import {
  PLAN_FREE,
  PRICE_TYPE_MONTHLY,
  PRICE_TYPE_YEARLY,
  normalizeSubscription,
  getPlanActions,
  isLegacyPlanType,
  ACTION_UPGRADE,
} from 'utils/billing.ts'
import {
  getRegionalDiscount,
  fetchRegionalDiscount,
} from 'ducks/regionalDiscount'
import { getCurrentOrganizationId } from 'ducks/organizations'
import { useFunnelTracking } from 'hooks/useFunnelTracking'

import { useFlags } from '../../../flags'

import NewPlanCard from './NewPlanCard'
import usePlanCards from './usePlanCards'

import ARROW_ICON_URL from './AnnualOffer-arrow.svg'
import './PlanOptionsBoard.scss'

const getButtonDisabled = isOrganizationAdmin => !isOrganizationAdmin

const getButtonTooltip = (isOrganizationAdmin, organizationName) => {
  if (isOrganizationAdmin) {
    return null
  }

  if (organizationName) {
    return `You must be an admin of the organization "${organizationName}" in order to upgrade.`
  } else {
    return 'You need to be an admin for this organization to change plans.'
  }
}

const PlanOptionsBoard = ({
  subscription = {},
  defaultAnnual,
  isOrganizationAdmin,
  organizationName,
  onPlanSelect,
}) => {
  const [annual, setAnnual] = useState(defaultAnnual)
  const dispatch = useDispatch()
  const regionalDiscount = useSelector(getRegionalDiscount)
  const organizationId = useSelector(getCurrentOrganizationId)
  const { trackClickedUpgrade } = useFunnelTracking()
  const flags = useFlags()
  const hasRegionalDiscounts = flags.hasRegionalDiscounts

  const { planType: currentPlanType, planInterval: currentPlanInterval } =
    normalizeSubscription(subscription)
  const planCards = usePlanCards(currentPlanType)

  const onToggle = () => setAnnual(!annual)

  const buttonDisabled = getButtonDisabled(isOrganizationAdmin)
  const buttonTooltip = getButtonTooltip(isOrganizationAdmin, organizationName)

  const planActions = getPlanActions(
    currentPlanType,
    currentPlanInterval,
    annual
  )

  const renderPlanCard = cardKey => {
    const { planType: cardPlanType, planTypeMatch } =
      planCards.cards[cardKey] || {}

    const isLegacyPlan = isLegacyPlanType(currentPlanType)

    const interval = annual ? PRICE_TYPE_YEARLY : PRICE_TYPE_MONTHLY

    const switchingInterval =
      cardPlanType !== PLAN_FREE && interval !== currentPlanInterval

    const isCurrentPlan =
      (currentPlanType === cardPlanType || currentPlanType === planTypeMatch) &&
      !switchingInterval

    const action = planActions[cardPlanType]

    const recommended = useMemo(() => {
      return cardKey === 'team'
    }, [cardKey])

    return (
      <NewPlanCard
        recommended={recommended}
        key={cardKey}
        cardKey={cardKey}
        annual={annual}
        current={isCurrentPlan}
        currentPlanType={currentPlanType}
        isLegacyPlan={isLegacyPlan}
        action={action}
        buttonDisabled={buttonDisabled}
        buttonTooltip={buttonTooltip}
        regionalDiscount={regionalDiscount}
        hasRegionalDiscounts={hasRegionalDiscounts}
        onSelect={() => {
          // Track upgrade button clicks
          if (action === ACTION_UPGRADE && organizationId) {
            const planTypeMapping = {
              // Exact matches for actual plan types used (2023 variants)
              starter2023: 'starter',
              professional2023: 'pro',
              business2023: 'business',
              team: 'team',

              // Legacy and fallback mappings
              starter: 'starter',
              starter2024: 'starter',
              pro: 'pro',
              professional: 'pro',
              professional2024: 'pro',
              team2024: 'team',
              business: 'business',
              business2022: 'business',
              business2024: 'business',
            }

            // Use exact match first, then lowercase fallback
            const mappedPlanType =
              planTypeMapping[cardPlanType] ||
              planTypeMapping[cardPlanType.toLowerCase()]

            if (mappedPlanType) {
              trackClickedUpgrade(organizationId, mappedPlanType).catch(
                error => {
                  console.error('Failed to track upgrade click:', error)
                }
              )
            } else {
              console.warn(`Unknown plan type for tracking: ${cardPlanType}`)
            }
          }

          onPlanSelect(cardPlanType, interval)
        }}
      />
    )
  }

  const orderedPlanCards = planCards.order.map(renderPlanCard)

  useEffect(() => {
    if (hasRegionalDiscounts) {
      dispatch(fetchRegionalDiscount(hasRegionalDiscounts))
    }
  }, [dispatch, hasRegionalDiscounts])

  return (
    <div className="PlanOptionsBoard">
      <div className="PlanOptionsBoard-intervalToggle">
        <p>Monthly</p>
        <ToggleButton value={annual} onChange={onToggle} />
        <p>Yearly</p>
        <div className="PlanOptionsBoard-annualOffer">
          <img
            className="PlanOptionsBoard-annualOffer-arrow"
            src={ARROW_ICON_URL}
            alt="Arrow pointing to annual toggle"
          />
          <p className="PlanOptionsBoard-annualOffer-text">
            Save 20% on yearly
          </p>
        </div>
      </div>
      <div className="PlanOptionsBoard-cards">{orderedPlanCards}</div>
    </div>
  )
}

export default PlanOptionsBoard
